import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import { Icon } from '@iconify/react';
// Page wrapper is now handled by the index component
import userAPI from 'utils/api/users';
import storage from 'utils/storage';
import ZoomControls from '../../components/ZoomControls';

export default function MyCompany() {
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [themeSongData, setThemeSongData] = useState(null);
  const [cocData, setCocData] = useState(null);
  const [handbookData, setHandbookData] = useState(null);
  const [missionVisionData, setMissionVisionData] = useState(null);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingThemeSong, setLoadingThemeSong] = useState(false);
  const [loadingCOC, setLoadingCOC] = useState(false);
  const [loadingHandbook, setLoadingHandbook] = useState(false);
  const [loadingMissionVision, setLoadingMissionVision] = useState(false);

  // URL validation functions
  const isYouTubeUrl = (url) => {
    if (!url || !url.trim()) return false;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return youtubeRegex.test(url.trim());
  };

  const isSpotifyUrl = (url) => {
    if (!url || !url.trim()) return false;
    const spotifyRegex = /^(https?:\/\/)?(www\.)?(open\.)?spotify\.com\/.+$/;
    return spotifyRegex.test(url.trim());
  };

  const isGoogleDriveUrl = (url) => {
    if (!url || !url.trim()) return false;
    const driveRegex = /^(https?:\/\/)?(www\.)?(drive\.google\.com)\/.+$/;
    return driveRegex.test(url.trim());
  };

  const isGoogleDocsUrl = (url) => {
    if (!url || !url.trim()) return false;
    const docsRegex = /^(https?:\/\/)?(www\.)?(docs\.google\.com)\/.+$/;
    return docsRegex.test(url.trim());
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const extractSpotifyId = (url) => {
    const match = url.match(/track\/([a-zA-Z0-9]+)/) || 
                 url.match(/playlist\/([a-zA-Z0-9]+)/) || 
                 url.match(/album\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  const getEmbedUrl = (url) => {
    if (isYouTubeUrl(url)) {
      const videoId = extractYouTubeId(url);
      // Add parameters to disable right-click and other features, but allow fullscreen
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&disablekb=1&iv_load_policy=3` : null;
    } else if (isGoogleDriveUrl(url)) {
      // Extract fileId and resourcekey if present
      const fileId = url.match(/[-\w]{25,}/);
      const resourceKeyMatch = url.match(/resourcekey=([^&]+)/);
      const resourceKey = resourceKeyMatch ? resourceKeyMatch[1] : null;
      
      if (fileId) {
        let embedUrl = `https://drive.google.com/file/d/${fileId[0]}/preview`;
        // Add resourcekey if present
        if (resourceKey) {
          embedUrl += `?resourcekey=${resourceKey}`;
        }
        return embedUrl;
      }
      return null;
    } else if (isSpotifyUrl(url)) {
      const spotifyId = extractSpotifyId(url);
      if (spotifyId) {
        if (url.includes('/track/')) {
          return `https://open.spotify.com/embed/track/${spotifyId}`;
        } else if (url.includes('/playlist/')) {
          return `https://open.spotify.com/embed/playlist/${spotifyId}`;
        } else if (url.includes('/album/')) {
          return `https://open.spotify.com/embed/album/${spotifyId}`;
        }
      }
    } else if (isGoogleDocsUrl(url)) {
      const docId = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
      const resourceKeyMatch = url.match(/resourcekey=([^&]+)/);
      const resourceKey = resourceKeyMatch ? resourceKeyMatch[1] : null;
      
      if (docId) {
        let embedUrl = `https://docs.google.com/document/d/${docId[1]}/preview`;
        // Add resourcekey if present
        if (resourceKey) {
          embedUrl += `?resourcekey=${resourceKey}`;
        }
        return embedUrl;
      }
      return null;
    }
    return null;
  };

  // Load data functions
  const loadVideos = async (company) => {
    try {
      setLoadingVideos(true);
      const videosResponse = await userAPI.get_videos_tutorial(company);
      if (videosResponse.ok) {
        setVideos(videosResponse.data.body);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoadingVideos(false);
    }
  };

  const loadThemeSong = async (company) => {
    try {
      setLoadingThemeSong(true);
      const response = await userAPI.get_theme_song({ company });
      if (response.data.success) {
        setThemeSongData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading theme song:', error);
    } finally {
      setLoadingThemeSong(false);
    }
  };

  const loadCOC = async (company) => {
    try {
      setLoadingCOC(true);
      const response = await userAPI.get_company_coc({ company });
      if (response.data) {
        setCocData(response.data);
      }
    } catch (error) {
      console.error('Error loading COC:', error);
    } finally {
      setLoadingCOC(false);
    }
  };

  const loadHandbook = async (company) => {
    try {
      setLoadingHandbook(true);
      const response = await userAPI.get_employee_handbook({ company });
      if (response.data.success) {
        setHandbookData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading handbook:', error);
    } finally {
      setLoadingHandbook(false);
    }
  };

  const loadMissionVision = async (company) => {
    try {
      setLoadingMissionVision(true);
      const response = await userAPI.get_mission_vision({ company });
      if (response.data.success) {
        setMissionVisionData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading mission vision:', error);
    } finally {
      setLoadingMissionVision(false);
    }
  };

  // Load all data on component mount
  useEffect(() => {
    const loadData = async () => {
      const local_user = await storage.getUser();
      if (!local_user) return;

      const userData = JSON.parse(local_user);
      setUser(userData);

      if (userData.company) {
        await Promise.all([
          loadVideos(userData.company),
          loadThemeSong(userData.company),
          loadCOC(userData.company),
          loadHandbook(userData.company),
          loadMissionVision(userData.company)
        ]);
      }
    };

    loadData();
  }, []);

  // Prevent default context menu behavior
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Create a secure iframe wrapper with zoom functionality
  const SecureIframe = ({ src, title, height, enableZoom = true }) => {
    const [zoom, setZoom] = useState(1);

    const handleZoomIn = () => {
      setZoom(prev => Math.min(prev + 0.2, 3));
    };

    const handleZoomOut = () => {
      setZoom(prev => Math.max(prev - 0.2, 0.5));
    };

    const handleReset = () => {
      setZoom(1);
    };

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: height || '400px',
          overflow: 'hidden',
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider'
        }}
        onContextMenu={handleContextMenu}
      >
        <iframe
          src={src}
          title={title || 'Embedded content'}
          width="100%"
          height="100%"
          frameBorder="0"
          allow="clipboard-write; encrypted-media; gyroscope; fullscreen"
          allowFullScreen={true}
          sandbox="allow-same-origin allow-scripts allow-forms allow-presentation"
          style={{
            pointerEvents: 'auto',
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            width: zoom !== 1 ? `${100 / zoom}%` : '100%',
            height: zoom !== 1 ? `${100 / zoom}%` : '100%'
          }}
        />
        {/* Invisible overlay that only prevents right-clicks but allows clicks to pass through */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'transparent',
            zIndex: 1,
            cursor: 'default',
            pointerEvents: 'none'
          }}
          onContextMenu={handleContextMenu}
        />

        {/* Zoom Controls */}
        {enableZoom && (
          <ZoomControls
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleReset}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <style jsx global>{`
          .secure-frame-container {
            position: relative;
          }
          .clickable-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1;
          }
          .content-container {
            position: relative;
          }
          .content-container::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;
            pointer-events: none;
          }
          /* Only prevent right-click action */
          .content-container::-webkit-scrollbar {
            width: 10px;
          }
          .content-container::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          .content-container::-webkit-scrollbar-thumb {
            background: #888;
          }
        `}</style>

        {/* Video Guidelines Section */}
        <Box sx={{ mb: 6, mt: 2 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 500,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Icon icon="mdi:video" style={{ fontSize: '28px' }} />
            Company Contents
          </Typography>
          <Grid container spacing={3}>
            {/* Video Tutorials Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icon icon="mdi:video" style={{ fontSize: '24px', marginRight: '8px' }} />
                    <Typography variant="h6">Company Video</Typography>
                  </Box>
                  {loadingVideos ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : videos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No video tutorials available.
                    </Typography>
                  ) : (
                    <List>
                      {videos.map((video) => (
                        <ListItem key={video._id}>
                          <ListItemText
                            primary={video.title.trim() || "No title"}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {video.description.trim() || "No description"}
                                </Typography>
                                {video.youtubeId && video.youtubeId.trim() ? (
                                  <Box 
                                    sx={{ mt: 1, width: '100%', height: '200px' }} 
                                    onContextMenu={handleContextMenu}
                                    className="content-container"
                                  >
                                    <SecureIframe
                                      src={getEmbedUrl(video.youtubeId)}
                                      title={video.title}
                                      height="200px"
                                    />
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    No video URL provided
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Theme Song Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icon icon="mdi:music" style={{ fontSize: '24px', marginRight: '8px' }} />
                    <Typography variant="h6">Company Theme Song</Typography>
                  </Box>
                  {loadingThemeSong ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : themeSongData && themeSongData.link ? (
                    <Box>
                      <Box 
                        sx={{ width: '100%', height: isSpotifyUrl(themeSongData.link) ? '80px' : '100px', mb: 2 }} 
                        onContextMenu={handleContextMenu}
                        className="content-container"
                      >
                        <SecureIframe
                          src={getEmbedUrl(themeSongData.link)}
                          title="Company Theme Song"
                          height={isSpotifyUrl(themeSongData.link) ? '80px' : '100px'}
                        />
                      </Box>
                      {(isGoogleDriveUrl(themeSongData.link) || isSpotifyUrl(themeSongData.link)) && (
                        <Typography variant="body2" color="text.secondary">
                          Note: You may need to enable cookies in your browser to view this content.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No theme song available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Company Guidelines Section */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 500,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Icon icon="mdi:office-building" style={{ fontSize: '28px' }} />
            Company Guidelines
          </Typography>
          <Grid container spacing={3}>
            {/* Code of Conduct Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icon icon="mdi:file-document" style={{ fontSize: '24px', marginRight: '8px' }} />
                    <Typography variant="h6">Code of Conduct</Typography>
                  </Box>
                  {loadingCOC ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : cocData && cocData.link ? (
                    <Box>
                      <Box 
                        sx={{ width: '100%', height: '400px', mb: 2 }} 
                        onContextMenu={handleContextMenu}
                        className="content-container"
                      >
                        <SecureIframe
                          src={getEmbedUrl(cocData.link)}
                          title="Code of Conduct"
                          height="400px"
                        />
                      </Box>
                      {isGoogleDriveUrl(cocData.link) && (
                        <Typography variant="body2" color="text.secondary">
                          Note: You may need to enable cookies in your browser to view this content.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No Code of Conduct available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Employee Handbook Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icon icon="mdi:book-open" style={{ fontSize: '24px', marginRight: '8px' }} />
                    <Typography variant="h6">Employee Handbook</Typography>
                  </Box>
                  {loadingHandbook ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : handbookData && handbookData.link ? (
                    <Box>
                      <Box 
                        sx={{ width: '100%', height: '400px', mb: 2 }} 
                        onContextMenu={handleContextMenu}
                        className="content-container"
                      >
                        <SecureIframe
                          src={getEmbedUrl(handbookData.link)}
                          title="Employee Handbook"
                          height="400px"
                        />
                      </Box>
                      {isGoogleDriveUrl(handbookData.link) && (
                        <Typography variant="body2" color="text.secondary">
                          Note: You may need to enable cookies in your browser to view this content.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No Employee Handbook available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Mission, Vision & Values Card */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icon icon="mdi:target" style={{ fontSize: '24px', marginRight: '8px' }} />
                    <Typography variant="h6">Mission, Vision & Values</Typography>
                  </Box>
                  {loadingMissionVision ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : missionVisionData && missionVisionData.link ? (
                    <Box>
                      <Box 
                        sx={{ width: '100%', height: '400px', mb: 2 }} 
                        onContextMenu={handleContextMenu}
                        className="content-container"
                      >
                        <SecureIframe
                          src={getEmbedUrl(missionVisionData.link)}
                          title="Mission, Vision & Values"
                          height="400px"
                        />
                      </Box>
                      {isGoogleDriveUrl(missionVisionData.link) && (
                        <Typography variant="body2" color="text.secondary">
                          Note: You may need to enable cookies in your browser to view this content.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No Mission, Vision & Core Values document available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
    </>
  );
}
